import { useState, useEffect } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '@/components/layout';
import { Button, Modal, ModalFooter } from '@/components/ui';
import { DeeplinkList, CreateDeeplinkModal } from '@/components/deeplink';
import { useDeeplinks } from '@/hooks/useDeeplinks';
import { getDeeplinkClicks } from '@/services/deeplinks';
import { useToast } from '@/components/ui';

const Deeplinks = () => {
  const { t } = useTranslation();
  const { deeplinks, loading, create, remove, toggleDisabled } = useDeeplinks();
  const { addToast } = useToast();

  const [clicksMap, setClicksMap] = useState<Record<string, number>>({});
  const [clicksLoading, setClicksLoading] = useState(false);

  useEffect(() => {
    if (deeplinks.length === 0) return;
    setClicksLoading(true);
    Promise.all(
      deeplinks.map(async (dl) => {
        const count = await getDeeplinkClicks(dl.id);
        return [dl.id, count] as const;
      })
    )
      .then((results) => {
        const map: Record<string, number> = {};
        for (const [id, count] of results) {
          map[id] = count;
        }
        setClicksMap(map);
      })
      .catch((err) => console.error('Failed to load click counts:', err))
      .finally(() => setClicksLoading(false));
  }, [deeplinks]);

  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleCreate = async (data: { originalUrl: string; name?: string }) => {
    await create(data);
    addToast({
      type: 'success',
      title: t('deeplinks.deeplinkCreated'),
      message: t('deeplinks.deeplinkCreatedMessage'),
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await remove(id);
      setDeleteConfirm(null);
      addToast({
        type: 'success',
        title: t('deeplinks.deeplinkDeleted'),
        message: t('deeplinks.deeplinkDeletedMessage'),
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: t('deeplinks.failedToDelete'),
        message: err instanceof Error ? err.message : t('common.somethingWentWrong'),
      });
    }
  };

  const handleToggleDisabled = async (id: string) => {
    try {
      const updated = await toggleDisabled(id);
      const isDisabled = updated.status === 'disabled';
      addToast({
        type: isDisabled ? 'warning' : 'success',
        title: isDisabled ? t('deeplinks.deeplinkDisabled') : t('deeplinks.deeplinkEnabled'),
        message: isDisabled ? t('deeplinks.deeplinkDisabledMessage') : t('deeplinks.deeplinkEnabledMessage'),
      });
    } catch (err) {
      addToast({
        type: 'error',
        title: t('common.somethingWentWrong'),
        message: err instanceof Error ? err.message : '',
      });
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">{t('deeplinks.title')}</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {deeplinks.length === 0
              ? t('deeplinks.emptyDescription')
              : t('deeplinks.count', { count: deeplinks.length })}
          </p>
        </div>
        <Button
          variant="primary"
          size="md"
          leftIcon={<Plus className="w-5 h-5" />}
          onClick={() => setIsCreateModalOpen(true)}
          className="flex-shrink-0"
        >
          {t('deeplinks.createDeeplink')}
        </Button>
      </div>

      {/* Deeplinks List */}
      <DeeplinkList
        deeplinks={deeplinks}
        clicksMap={clicksMap}
        clicksLoading={clicksLoading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onDelete={(id) => setDeleteConfirm(id)}
        onToggleDisabled={handleToggleDisabled}
        onCreateNew={() => setIsCreateModalOpen(true)}
      />

      {/* Create Modal */}
      <CreateDeeplinkModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={t('deeplinks.deleteDeeplink')}
        description={t('deeplinks.deleteConfirmation')}
        size="sm"
      >
        <ModalFooter>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            className="bg-red-500 hover:bg-red-600"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
          >
            {t('common.delete')}
          </Button>
        </ModalFooter>
      </Modal>
    </DashboardLayout>
  );
};

export default Deeplinks;
